import { useMutation } from '@apollo/client';
import React from 'react';
import { match } from 'ts-pattern';

import { useCompleteFragment } from '@eluve/apollo-client';
import {
  Combobox,
  ComboboxDropdown,
  ComboboxOption,
  ComboboxSelectButton,
  ComboboxSelectCheck,
  Icon,
  Stack,
  Text,
  useToast,
} from '@eluve/components';
import {
  appointmentSessionTypeFragment,
  useAppointmentId,
  useAppointmentSessionType,
} from '@eluve/frontend-appointment-hooks';
import { graphql } from '@eluve/graphql.tada';
import { useTranslation } from '@eluve/i18n';
import { useTenantIdFromParams } from '@eluve/session-helpers';
import { tenantSessionTypesFragment } from '@eluve/shared-gql-operations';

const setAppointmentSessionTypeMutation = graphql(
  `
    mutation setAppointmentSessionType(
      $tenantId: uuid!
      $appointmentId: uuid!
      $sessionTypeId: uuid
    ) {
      updateAppointmentsByPk(
        pkColumns: { tenantId: $tenantId, id: $appointmentId }
        _set: { sessionTypeId: $sessionTypeId }
      ) {
        ...SessionType
      }
    }
  `,
  [appointmentSessionTypeFragment],
);

export interface SessionTypeSelectorProps {
  disabled?: boolean;
}

export const SessionTypeSelector: React.FC<SessionTypeSelectorProps> = ({
  disabled = false,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const tenantId = useTenantIdFromParams() ?? '';
  const appointmentId = useAppointmentId();

  const appointmentSessionType = useAppointmentSessionType();

  const { session_types: types } = useCompleteFragment({
    fragment: tenantSessionTypesFragment,
    key: {
      id: tenantId,
    },
  });

  const selectedsessionType = types.find(
    (type) => type.id === appointmentSessionType?.id,
  );

  const [setAppointmentSessionType] = useMutation(
    setAppointmentSessionTypeMutation,
    {
      optimisticResponse: (input) => ({
        updateAppointmentsByPk: {
          __typename: 'Appointments' as const,
          id: appointmentId,
          session_type: input.sessionTypeId
            ? {
                __typename: 'SessionTypes' as const,
                id: input.sessionTypeId ?? '',
                name:
                  types.find((type) => type.id === input.sessionTypeId)?.name ??
                  '',
              }
            : null,
        },
      }),
      onError: () =>
        toast.error(t('appointmentTools.sessionType.updateFailed')),
    },
  );

  const handleSessionTypeChange = (typeId: string | undefined) => {
    setAppointmentSessionType({
      variables: {
        tenantId,
        appointmentId,
        sessionTypeId: typeId,
      },
    });
  };

  return (
    <Combobox disabled={disabled || types.length === 0}>
      <ComboboxSelectButton
        className="mb-2 w-full overflow-hidden border-transparent hover:bg-bg-quaternary"
        title={selectedsessionType?.name}
      >
        <Stack className="overflow-hidden" direction="horizontal" gap={2}>
          <Icon name="Tag" />
          <Text className="flex-1 truncate text-left">
            {match(types.length)
              .with(0, () => t('appointmentTools.sessionType.noneAvailable'))
              .otherwise(
                () =>
                  selectedsessionType?.name ??
                  t('appointmentTools.sessionType.noType'),
              )}
          </Text>
        </Stack>
      </ComboboxSelectButton>
      <ComboboxDropdown
        searchPlaceholder={t('appointmentTools.sessionType.searchTypes')}
      >
        {types.map((type) => (
          <ComboboxOption
            key={type.id}
            onSelect={() => handleSessionTypeChange(type.id)}
          >
            <ComboboxSelectCheck
              className="mr-2"
              selected={selectedsessionType?.id === type.id}
            />
            {type.name}
          </ComboboxOption>
        ))}
        <ComboboxOption onSelect={() => handleSessionTypeChange(undefined)}>
          <ComboboxSelectCheck
            selected={selectedsessionType === undefined}
            className="mr-2"
          />
          {t('appointmentTools.sessionType.noType')}
        </ComboboxOption>
      </ComboboxDropdown>
    </Combobox>
  );
};
